import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where, limit as fsLimit } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useAuth } from '../../../modules/auth/core/Auth';

/**
 * useAndreiReminders — live feed of FIRED Andrei wake-actions surfaced on the
 * dashboard Attention Queue. These are written by the wake engine into
 * `attention_items` (source:'andrei-task', status:'open') when a scheduled task
 * fires; the owner also gets the same message in their Andrei chat.
 *
 * Kept separate from useAttentionItems (which is a pure client computation over
 * live business data) so it doesn't touch the shared AttentionItem union or the
 * graphite feedModel's exhaustive switch. Dismissing a reminder writes
 * status:'resolved' straight to the doc (attention_items is client-writable).
 */
export interface AndreiReminder {
  id: string;
  title: string;
  detail: string;
  statusCode: string;       // OVERDUE | WAIT (drives the row colour)
  triggerType: string;
  sourceTaskId: string;
  createdAtMs: number;
}

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  const s = typeof ts.seconds === 'number' ? ts.seconds : ts._seconds;
  return typeof s === 'number' ? s * 1000 : 0;
}

export function useAndreiReminders(): AndreiReminder[] {
  const { currentUser } = useAuth();
  const uid = currentUser?.id;
  const [items, setItems] = useState<AndreiReminder[]>([]);

  useEffect(() => {
    if (!uid) { setItems([]); return; }
    // Equality-only filters (no composite index needed). Owner-scoped.
    const q = query(
      collection(db, 'attention_items'),
      where('source', '==', 'andrei-task'),
      where('ownerUid', '==', uid),
      where('status', '==', 'open'),
      fsLimit(50),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            id: d.id,
            title: String(data.title || 'Reminder'),
            detail: String(data.detail || ''),
            statusCode: String(data.statusCode || 'WAIT'),
            triggerType: String(data.triggerType || ''),
            sourceTaskId: String(data.sourceTaskId || ''),
            createdAtMs: tsToMs(data.createdAt),
          } as AndreiReminder;
        });
        rows.sort((a, b) => b.createdAtMs - a.createdAtMs);
        setItems(rows);
      },
      (err) => { console.warn('[useAndreiReminders] sub error:', err); setItems([]); },
    );
    return () => unsub();
  }, [uid]);

  return items;
}
